/**
 * ProtectedRoute Component
 * 
 * Route guard that redirects unauthenticated users to login.
 * Also handles profile completion redirect for new users.
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth, useNeedsProfileCompletion } from '../../hooks/useAuth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** If true, also requires profile to be completed */
  requireProfile?: boolean;
  /** If true, requires admin role */
  requireAdmin?: boolean;
  /** If true, requires owner or admin role */
  requireOwner?: boolean;
}

/**
 * Protects routes that require authentication
 * 
 * @example
 * <Route path="/profile" element={
 *   <ProtectedRoute>
 *     <ProfilePage />
 *   </ProtectedRoute>
 * } />
 */
export function ProtectedRoute({
  children,
  requireProfile = false,
  requireAdmin = false,
  requireOwner = false,
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, profile } = useAuth();
  const needsProfileCompletion = useNeedsProfileCompletion();
  const location = useLocation();

  // Show nothing while checking auth status
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-wine-600" />
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    // Save the attempted URL for redirecting after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check admin requirement
  if (requireAdmin && profile?.accountType !== 'admin') {
    return <Navigate to="/" replace />;
  }

  // Check owner requirement
  if (requireOwner && profile?.accountType !== 'owner' && profile?.accountType !== 'admin') {
    return <Navigate to="/" replace />;
  }

  // Authenticated but needs to complete profile
  // Don't redirect if already on complete-profile page
  if (requireProfile && needsProfileCompletion && location.pathname !== '/complete-profile') {
    return <Navigate to="/complete-profile" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

/**
 * Route that should only be accessible to guests (not logged in)
 * Redirects authenticated users to home or their intended destination
 */
export function GuestRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-wine-600" />
      </div>
    );
  }

  if (isAuthenticated) {
    // Redirect to the page they were trying to access, or home
    const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/';
    return <Navigate to={from} replace />;
  }

  return <>{children}</>;
}
